'use client';

import { useEffect, useState } from 'react';
import { Box, Stack, Typography, Button } from '@mui/material';
import { PhotoCamera, ContentPaste } from '@mui/icons-material';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

interface ImageUploaderProps {
  machineId: string;
  onUpload: (machineId: string, file: File) => Promise<void>;
  disabled?: boolean;
}

export function ImageUploader({ machineId, onUpload, disabled }: ImageUploaderProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      types: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'],
      drop: async (item: { files: File[] } | any) => {
        if (disabled) return;

        let files: File[] = [];

        // Handle files dropped from file system
        if (item.files) {
          files = Array.from(item.files);
        }
        // Handle HTML dropped from browser
        else if (item.html) {
          try {
            const match = item.html.match(/<img.+?src=["'](.+?)["']/);
            if (match && match[1]) {
              const response = await fetch(match[1]);
              const blob = await response.blob();
              if (blob.type.startsWith('image/')) {
                const file = new File([blob], `image.${blob.type.split('/')[1]}`, {
                  type: blob.type,
                });
                files = [file];
              }
            }
          } catch (error) {
            console.error('Error processing dragged image:', error);
          }
        }

        // Process the first valid image file
        const imageFile = files.find(file => file.type.startsWith('image/'));
        if (imageFile) {
          await onUpload(machineId, imageFile);
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [machineId, onUpload, disabled]
  );

  // Handle file input change
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !event.target.files?.length) return;

    const file = event.target.files[0];
    if (file.type.startsWith('image/')) {
      await onUpload(machineId, file);
      setInputKey(prev => prev + 1);
    }
  };

  // Add keyboard paste handler
  useEffect(() => {
    // Handle paste from clipboard
    const handlePaste = async (event?: ClipboardEvent | React.MouseEvent) => {
      try {
        // If it's a paste event (Ctrl+V), use the event data
        if (event && 'clipboardData' in event && event.clipboardData) {
          const items = Array.from(event.clipboardData.items);
          const imageItem = items.find(item => item.type.startsWith('image/'));
          if (imageItem) {
            const file = imageItem.getAsFile();
            if (file) {
              await onUpload(machineId, file);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error pasting image:', error);
      }
    };
    const handleKeyboardPaste = (e: ClipboardEvent) => {
      if (isHovering && !disabled) {
        handlePaste(e);
      }
    };

    window.addEventListener('paste', handleKeyboardPaste);
    return () => window.removeEventListener('paste', handleKeyboardPaste);
  }, [isHovering, disabled, machineId, onUpload]);

  // Update drag active state
  useEffect(() => {
    setIsDragActive(isOver && canDrop);
  }, [isOver, canDrop]);

  return (
    <Box
      ref={drop}
      onClick={() => !disabled && document.getElementById(`file-input-${machineId}`)?.click()}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: disabled ? 'transparent' : 'rgba(0,0,0,0.1)',
        },
        ...(isDragActive && {
          bgcolor: 'rgba(25, 118, 210, 0.1)',
          border: '2px dashed #1976d2',
          '& .upload-hint': {
            opacity: 1,
          },
        }),
      }}
    >
      <input
        id={`file-input-${machineId}`}
        key={inputKey}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Stack
        spacing={1}
        alignItems="center"
        className="upload-hint"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: isHovering || isDragActive ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
          bgcolor: 'rgba(255,255,255,0.9)',
          p: 2,
          borderRadius: 1,
          boxShadow: 1,
          textAlign: 'center',
        }}
      >
        <PhotoCamera sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="body2" color="primary">
          {isDragActive ? 'Drop image here' : 'Click, drag, or paste image'}
        </Typography>
      </Stack>
    </Box>
  );
}
